import { useCallback, useEffect, useState } from "react";
import { Link } from "wouter";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Modal } from "../../components/Modal";
import { Skeleton } from "../../components/Spinner";
import { apiGet } from "../../lib/customFetch";
import { ROLLE_TEKST, type UserRole } from "../../lib/useMe";
import {
  AdminProblem,
  beskrivAdminProblem,
  type AdminProblemValue,
} from "./AdminProblem";
import { formatMinutter, klokken } from "./format";
import styles from "./admin.module.css";

export interface TodayRow {
  id: string;
  navn: string;
  /** Additiv rollemarkering: ejere kan også føre egne timer i små firmaer. */
  rolle?: UserRole;
  mode: "stempel" | "standard";
  aabenSiden: string | null;
  iDagMinutter: number;
  /** Additive pause-felter (fase 2a) — optional for bagudkompatibilitet. */
  pauseMinutter?: number;
  pauseKilde?: "auto" | "manuel" | "ingen";
  nettoMinutter?: number;
}

export interface ComplianceRow {
  id: string;
  navn: string;
  niveau: "groen" | "gul" | "roed";
  fund: { regel: string; niveau: "gul" | "roed"; besked: string }[];
}

const NIVEAU_TEKST: Record<ComplianceRow["niveau"], string> = {
  groen: "OK",
  gul: "Advarsel",
  roed: "Brud",
};

type ViewState =
  | { status: "loading" }
  | { status: "ok"; rows: TodayRow[] }
  | { status: "failed"; problem: AdminProblemValue };

const POLL_MS = 30_000;
const TICK_MS = 10_000;

/**
 * Vist tid = serverens afsluttede minutter + løbende tid for en åben
 * stempling, beregnet fra aabenSiden-instantet (0m-fixet, jf. decision
 * 0005: API-feltet iDagMinutter tæller KUN afsluttede perioder —
 * kontrakten er frossen, så klienten lægger selv løbende tid til).
 */
function visteMinutter(row: TodayRow, nu: number): number {
  const loebende =
    row.aabenSiden === null
      ? 0
      : Math.max(0, Math.floor((nu - new Date(row.aabenSiden).getTime()) / 60_000));
  return row.iDagMinutter + loebende;
}

const GLEMT_UDSTEMPLING_MS = 12 * 60 * 60 * 1000;

/**
 * M-01b: ren frontend-beregning (forløber for M-23's proaktive forslag) —
 * ingen API-ændring, ingen compliance-motor-involvering.
 */
function erGlemtUdstempling(row: TodayRow, nu: number): boolean {
  return (
    row.aabenSiden !== null &&
    nu - new Date(row.aabenSiden).getTime() > GLEMT_UDSTEMPLING_MS
  );
}

/**
 * "I dag": alle medarbejdere med live status. Polling hvert 30. sekund,
 * pauset når fanen er skjult (document.visibilityState) — websockets er
 * bevidst fravalgt (decision 0005).
 */
export function TodayView() {
  const [state, setState] = useState<ViewState>({ status: "loading" });
  const [staleProblem, setStaleProblem] = useState<AdminProblemValue | null>(null);
  const [nu, setNu] = useState(() => Date.now());
  // Fase 4a: compliance-status (regelmotoren) — hentes én gang; den
  // ændrer sig langsomt og skal ikke med i 30-sekunders-pollingen.
  const [compliance, setCompliance] = useState<Map<string, ComplianceRow> | null>(null);
  const [valgt, setValgt] = useState<ComplianceRow | null>(null);

  useEffect(() => {
    apiGet<ComplianceRow[]>("/api/clockin/admin/compliance")
      .then((rows) => setCompliance(new Map(rows.map((r) => [r.id, r]))))
      .catch(() => setCompliance(null));
  }, []);

  // Klient-tick: løbende tid opdateres MELLEM pollingerne uden fetch.
  useEffect(() => {
    const timer = window.setInterval(() => setNu(Date.now()), TICK_MS);
    return () => window.clearInterval(timer);
  }, []);

  const load = useCallback(() => {
    apiGet<TodayRow[]>("/api/clockin/admin/today")
      .then((rows) => {
        setStaleProblem(null);
        setState({ status: "ok", rows });
      })
      .catch((error: unknown) => {
        const problem = beskrivAdminProblem(error, "hente dagens status");
        setStaleProblem(problem);
        setState((prev) =>
          prev.status === "ok" ? prev : { status: "failed", problem },
        );
      });
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  if (state.status === "loading") {
    // Skeleton med listens kendte form — intet spring når data lander.
    return (
      <div className={styles.dagListe} aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <div key={i} className={styles.dagRaekke}>
            <Skeleton width="10px" height="10px" />
            <Skeleton width={`${String(38 - i * 6)}%`} height="1rem" />
            <Skeleton width="4rem" height="0.875rem" />
          </div>
        ))}
      </div>
    );
  }
  if (state.status === "failed") {
    return (
      <div role="alert">
        <EmptyState
          title={state.problem.title}
          description={state.problem.description}
          cta={<Button onClick={load}>Prøv igen</Button>}
        />
      </div>
    );
  }

  const staleFeedback =
    staleProblem === null ? null : (
      <AdminProblem problem={staleProblem} onAction={load} />
    );
  const nogenInde = state.rows.some((r) => r.aabenSiden !== null);
  const nogenTimer = state.rows.some((r) => r.iDagMinutter > 0);
  const medarbejdere = state.rows.filter((row) => row.rolle === "medarbejder");
  const kunStandard =
    medarbejdere.length > 0 &&
    medarbejdere.every((row) => row.mode === "standard");
  if (!nogenInde && !nogenTimer) {
    return (
      <>
        {staleFeedback}
        {kunStandard ? (
          <EmptyState
            illustration="📅"
            title="Teamet bruger faste standarduger"
            description="Medarbejderne registrerer kun afvigelser. Åbn ugevisningen for at se timer, fravær og rettelser."
          />
        ) : (
          <EmptyState
            illustration="☕"
            title="Ingen er stemplet ind lige nu"
            description="Når nogen stempler ind på telefonen, dukker de op her — helt af sig selv."
          />
        )}
      </>
    );
  }

  return (
    <>
      {staleFeedback}
      <div className={styles.dagListe}>
        {state.rows.map((r) => {
          const inde = r.aabenSiden !== null;
          const standard = r.mode === "standard";
          const kompl = compliance?.get(r.id);
          const glemt = erGlemtUdstempling(r, nu);
          return (
            <div key={r.id} className={styles.dagRaekke}>
              <span
                className={`${styles.prik} ${
                  standard
                    ? styles.prikStandard ?? ""
                    : inde
                      ? styles.prikInde ?? ""
                      : ""
                }`}
                aria-label={
                  standard
                    ? "Standarduge"
                    : inde
                      ? "Stemplet ind"
                      : "Ikke stemplet ind"
                }
              />
              <span className={styles.dagNavn}>
                {r.navn}
                {r.rolle !== undefined && r.rolle !== "medarbejder" && (
                  <span className={styles.rolleBadge}>
                    {ROLLE_TEKST[r.rolle].toLocaleLowerCase("da-DK")}
                  </span>
                )}
                {kompl !== undefined && (
                  <button
                    type="button"
                    className={`${styles.komplBadge} ${
                      kompl.niveau === "roed"
                        ? styles.komplRoed ?? ""
                        : kompl.niveau === "gul"
                          ? styles.komplGul ?? ""
                          : styles.komplGroen ?? ""
                    }`}
                    onClick={() => setValgt(kompl)}
                    aria-label={`Arbejdstidsregler for ${r.navn}: ${NIVEAU_TEKST[kompl.niveau]}`}
                  >
                    {NIVEAU_TEKST[kompl.niveau]}
                  </button>
                )}
                {glemt && (
                  <Link
                    href="/clockin?fane=uge"
                    className={styles.glemtBadge}
                    title="Åben stempling i over 12 timer"
                    aria-label={`Husk udstempling for ${r.navn} — åbn ugevisningen`}
                  >
                    Husk udstempling?
                  </Link>
                )}
              </span>
              <span className={styles.dagStatus}>
                {standard
                  ? "fast plan · afvigelser i Uge"
                  : inde
                    ? `inde siden ${klokken.format(new Date(r.aabenSiden!))}`
                    : "ikke stemplet ind"}
              </span>
              <span className={styles.dagTimer}>
                {standard ? (
                  <Link href="/clockin?fane=uge" className={styles.ugeLink}>
                    Se uge
                  </Link>
                ) : (
                  <>
                    {formatMinutter(visteMinutter(r, nu))}
                    {(r.pauseMinutter ?? 0) > 0 && (
                      <span className={styles.pauseHint}>
                        − {formatMinutter(r.pauseMinutter!)} pause
                      </span>
                    )}
                  </>
                )}
              </span>
            </div>
          );
        })}
      </div>

      {valgt !== null && (
        <Modal title={`Arbejdstidsregler — ${valgt.navn}`} onClose={() => setValgt(null)}>
          {valgt.fund.length === 0 ? (
            <p className={styles.komplTom}>
              Ingen fund: 11-timers hvile, ugentligt fridøgn og 48-timers
              gennemsnittet ser fine ud.
            </p>
          ) : (
            <ul className={styles.komplListe}>
              {valgt.fund.map((f, i) => (
                <li
                  key={i}
                  className={f.niveau === "roed" ? styles.komplFundRoed ?? "" : styles.komplFundGul ?? ""}
                >
                  {f.besked}
                </li>
              ))}
            </ul>
          )}
        </Modal>
      )}
    </>
  );
}
